<template>
  <q-menu
    ref="menu"
    :model-value="true"
    no-parent-event
    persistent
    no-focus
    no-refocus
    :anchor="narrow ? 'bottom left' : 'top right'"
    self="top left"
    :offset="narrow ? [0, 4] : [8, 0]"
    max-width="min(560px, calc(100vw - 16px))"
    transition-show="fade"
    transition-hide="fade"
    class="tracksheet-marker-passages-popover"
    data-testid="tracksheet-marker-passages-popover"
    @pointerenter="$emit('popover-enter')"
    @pointerleave="$emit('popover-leave')"
    @pointerdown="$emit('interact')"
  >
    <div class="q-pa-sm tracksheet-marker-passages-popover__body">
      <div class="row items-center no-wrap q-pb-xs">
        <div class="text-subtitle2 ellipsis">{{ markerName }}</div>
        <q-badge
          v-if="loaded"
          color="blue-brand"
          text-color="white"
          class="q-ml-sm"
        >
          {{ popover.total }}
        </q-badge>
        <q-space />
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="close"
          aria-label="Close passages"
          data-testid="tracksheet-marker-passages-popover-close"
          @click="$emit('close')"
        />
      </div>

      <div v-if="!loaded" class="text-caption text-grey-7 q-pa-sm">
        Loading passages…
      </div>
      <div
        v-else-if="popover.total === 0"
        class="text-caption text-grey-7 q-pa-sm"
        data-testid="tracksheet-marker-passages-popover-empty"
      >
        No passages at this marker yet
      </div>
      <template v-else>
        <div class="text-caption text-grey-7 q-pb-xs">
          Newest {{ popover.rows.length }} of {{ popover.total }}
        </div>
        <PassagesTable
          :rows="popover.rows"
          :columns="columns"
          :row-key="rowKey"
          :sort-method="keepOrder"
          :pagination="{ rowsPerPage: 0 }"
          hide-bottom
          :dialog-source="dialogSource"
          :pig-run-id="pigRunId"
          :active="chartStepsHere"
          :row-opens-chart="narrow"
        />
        <div class="row justify-end q-pt-xs">
          <q-btn
            flat
            dense
            no-caps
            color="blue-brand"
            icon-right="south"
            :label="showAllText"
            data-testid="tracksheet-marker-passages-popover-show-all"
            @click="$emit('show-all', pipelineSectionMarkerId)"
          />
        </div>
      </template>
    </div>
  </q-menu>
</template>

<script lang="ts">
/**
 * EN-1851: a tracksheet marker's newest passages (hover on desktop, the
 * passages icon on touch). The rows are the run passages the section below
 * already loaded, so opening it makes no request. Waveform, verify and ML
 * work as in the section (PassagesTable); the chart's next / previous walks
 * these rows only when the chart was opened here.
 */
import { defineComponent, PropType } from 'vue';
import { IRunPassage } from '@/models/Passage';
import PassagesTable from '@/components/PassagesTable.vue';
import {
  MarkerPopoverRows,
  PopoverColumn,
  TRACKSHEET_POPOVER_SOURCE,
  followTableResize,
  listenForEscape,
  markerPopoverColumns,
  markerPopoverRows,
  respondsToSwitchPassage,
  showAllLabel,
} from '@/utils/MarkerPassagesPopover';
import { RunPassageTimeZone, runPassageRowKey } from '@/utils/RunPassages';

export default defineComponent({
  name: 'TracksheetMarkerPassagesPopover',
  components: { PassagesTable },
  props: {
    pipelineSectionMarkerId: { type: Number, required: true },
    markerName: { type: String, default: '' },
    pigRunId: { type: Number as PropType<number | null>, default: null },
    /** Every passage on the run, as loaded by the section below. */
    runPassages: { type: Array as PropType<IRunPassage[]>, default: () => [] },
    loaded: { type: Boolean, default: false },
    /** The tracksheet's Pass Time setting. */
    timeZone: {
      type: String as PropType<RunPassageTimeZone>,
      default: 'utc',
    },
  },
  emits: [
    'close',
    'show-all',
    'popover-enter',
    'popover-leave',
    'interact',
    'popover-unmounted',
  ],
  data() {
    return {
      dialogSource: TRACKSHEET_POPOVER_SOURCE,
      stopEscape: null as (() => void) | null,
      stopResize: null as (() => void) | null,
    };
  },
  computed: {
    narrow(): boolean {
      return this.$q.screen.lt.md;
    },
    columns(): PopoverColumn[] {
      return markerPopoverColumns(this.timeZone, this.narrow);
    },
    popover(): MarkerPopoverRows {
      return markerPopoverRows(this.runPassages, this.pipelineSectionMarkerId);
    },
    showAllText(): string {
      return showAllLabel(this.popover.total);
    },
    chartStepsHere(): boolean {
      return respondsToSwitchPassage(
        this.dialogSource,
        this.$store.state.passage?.passageDialogSource
      );
    },
  },
  mounted() {
    // The menu has no focus of its own, so Esc is heard on the document.
    this.stopEscape = listenForEscape(document, () => this.$emit('close'));
    // The drawer's width moves the marker cell: follow it.
    const anchor = (this.$el as Node | null)?.parentElement;
    this.stopResize = followTableResize(
      anchor?.closest('.q-table__middle') ?? null,
      () => (this.$refs.menu as any)?.updatePosition?.()
    );
  },
  beforeUnmount() {
    this.stopEscape?.();
    this.stopEscape = null;
    this.stopResize?.();
    this.stopResize = null;
    // Sent on every unmount (close, another marker, the row leaving the
    // table). The worksheet closes the popover only if this marker is still
    // the open one, i.e. its row left the table (another page, the filter),
    // so it does not come back with the row.
    this.$emit('popover-unmounted', this.pipelineSectionMarkerId);
  },
  methods: {
    rowKey(row: IRunPassage): string {
      return runPassageRowKey(row);
    },
    /** The rows are already newest first. */
    keepOrder(rows: readonly IRunPassage[]): IRunPassage[] {
      return rows as IRunPassage[];
    },
  },
});
</script>

<style scoped>
.tracksheet-marker-passages-popover__body {
  min-width: min(320px, calc(100vw - 16px));
}
/* EN-1851: on a phone, date over time and stacked ML badges (as the section). */
.tracksheet-marker-passages-popover__body
  :deep(.tracksheet-marker-passages-popover__time) {
  white-space: normal;
  min-width: 88px;
  max-width: 112px;
}
.tracksheet-marker-passages-popover__body
  :deep(.tracksheet-marker-passages-popover__ml) {
  white-space: normal;
  min-width: 72px;
  max-width: 84px;
}
.tracksheet-marker-passages-popover__body
  :deep(.tracksheet-marker-passages-popover__ml > *) {
  margin: 1px 0;
}
</style>
